<script setup lang="ts">
import { userModule } from '@/store/user/module'
import { computed, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { analyticsModule } from '@/store/analytics/module'
import { toastModule } from '@/store/toast/module'
import { examMetadataModule } from '@/store/examMetadata/module'

const { ModalContainer, Radio, Textarea: PocketTextarea, Button: PocketButton, Icon } = UIKit

const { concept } = defineProps<{
    concept: string
}>()

const emit = defineEmits<{
    'close': []
}>()

const relevant = ref<typeof relevantOptions[number] | null>(null)
const relevantOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
    { label: 'I\'m not sure', value: 'unsure' },
]
const customAnswer = ref('')

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const isSubmitDisabled = computed(() => {
    return !relevant.value
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const submitForm = () => {
    analyticsModule.actions.amplitudeTrack('First Concept Quiz Survey Answered', {
        concept: concept,
        examGuid: currentExamMetadata.value?.examGuid,
        isConceptQuizRelevant: relevant.value?.label,
        openFeedback: customAnswer.value,
    })

    toastModule.actions.displayToast({
        title: 'Thank you for your feedback!',
    })

    emit('close')
}

</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="conceptQuizSurvey"
            :is-dark-mode="isDarkMode"
            :show-close-button="true"
            @close="emit('close')"
        >
            <div class="concept-quiz-survey">
                <div class="concept-quiz-survey__title-section">
                    <Icon
                        type="concept"
                        class="concept-quiz-survey__target-icon"
                        v-dark
                    />
                    <div class="concept-quiz-survey__title">
                        How was your quiz on {{ concept || 'this concept' }}?
                    </div>
                    <div class="concept-quiz-survey__subtitle" v-dark>
                        We'd love your feedback.
                    </div>
                </div>
                <div class="concept-quiz-survey__form">
                    <Radio
                        v-model="relevant"
                        :data="relevantOptions"
                        :is-dark-mode="isDarkMode"
                        class="concept-quiz-survey__field"
                        label="Did this quiz feel relevant to what you need to study?"
                    />
                    <PocketTextarea
                        v-model="customAnswer"
                        label="Anything else you'd like to share?"
                        class="concept-quiz-survey__field concept-quiz-survey__field-input"
                        :is-dark-mode="isDarkMode"
                        height="62"
                        width=""
                        @keydown.enter="submitForm"
                    />
                </div>
                <div class="concept-quiz-survey__actions">
                    <PocketButton
                        class="concept-quiz-survey__dismiss-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emit('close')"
                        type="secondary"
                    >
                        Cancel
                    </PocketButton>
                    <PocketButton
                        class="concept-quiz-survey__submit-btn"
                        :disabled="isSubmitDisabled"
                        :is-dark-mode="isDarkMode"
                        @click="submitForm"
                    >
                        Submit
                    </PocketButton>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.concept-quiz-survey {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 520px;

    @include breakpoint(black-bear) {
        width: 100%;
        margin: 0 auto;
    }

    &__target-icon {
        margin-bottom: 8px;
        color: $ash;

        &--dark {
            color: $pewter;
        }
    }

    &__title-section {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 24px 32px 30px;
        box-sizing: border-box;
        border-bottom: 1px solid $fog;
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        line-height: 25px;
        letter-spacing: 0.3px;
    }

    &__subtitle {
        margin-top: 6px;
        color: $ash;
        text-align: center;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;

        &--dark {
            color: $barely-background;
        }
    }

    &__form {
        padding: 30px 32px;
        box-sizing: border-box;
        width: 100%;
    }

    &__actions {
        margin-top: 10px;
        align-self: center;
        padding: 0 40px 32px;
    }

    &__submit-btn {
        margin-left: 8px;
    }

    &__field :deep(.uikit-radio__label),
    &__field :deep(.uikit-textarea__label) {
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        color: $brand-black;

        &.uikit-radio__label--focus,
        &.uikit-textarea__label--focus {
            color: $brand-blue;
        }

        &.uikit-radio__label--hover {
            color: $brand-black;
        }

        &.uikit-radio__label--dark,
        &.uikit-textarea__label--dark {
            color: $fog;

            &.uikit-radio__label--focus,
            &.uikit-textarea__label--focus {
                color: $banana-bread;
            }

            &.uikit-radio__label--hover {
                color: $fog;
            }
        }
    }

    &__field-input {
        margin-top: 30px;
    }
}
</style>
